// utils/apiClient.ts
/**
 * Enhanced API Client Factory
 *
 * A unified API client with:
 * - Automatic JWT authentication via Supabase
 * - Request/response interceptors
 * - Retry logic with exponential backoff
 * - Configurable timeouts
 * - Standardized error handling
 * - Response unwrapping
 *
 * Usage:
 *   import { apiClient } from './apiClient';
 *
 *   // Simple GET
 *   const data = await apiClient.get('/api/courses');
 *
 *   // POST with body
 *   const result = await apiClient.post('/api/plans', { name: 'My Plan' });
 *
 *   // With options
 *   const data = await apiClient.get('/api/plans', {
 *     timeout: 5000,
 *     retry: { attempts: 3 }
 *   });
 */

import { API_URL, API_TIMEOUT_DEFAULT, API_RETRY_ATTEMPTS, API_RETRY_DELAY } from './constants';
import { getValidAccessToken, isPermanentRefreshError, forceRefreshToken } from '../lib/tokenManager';
import { handleAuthError } from './authErrorHandler';
import type { ApiErrorCode, ErrorType } from '@/types';

// =============================================================================
// TYPES
// =============================================================================

export interface RetryConfig {
  attempts: number;
  delay: number;
  retryableStatuses: number[];
  retryOnNetworkError: boolean;
  /**
   * Re-send a NON-idempotent request (POST/PUT/PATCH/DELETE) after a transport
   * failure — a dropped connection or a client-side timeout. Defaults to false,
   * because such a failure does not tell us whether the server processed the
   * request: the mutation may have committed and only the response been lost, so
   * retrying can duplicate it. Set true only for an endpoint you know is
   * genuinely idempotent (or one that de-duplicates server-side).
   *
   * This does not affect status-based retries (`retryableStatuses`): those
   * required a response, so the request demonstrably reached the server.
   */
  retryUnsafeMethods?: boolean;
}

/**
 * HTTP methods with no side effects, so re-sending one after a transport failure
 * is always safe (RFC 9110 §9.2.1).
 */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export interface ApiClientConfig {
  baseUrl: string;
  timeout: number;
  retry: RetryConfig;
  headers: Record<string, string>;
}

export interface RequestConfig {
  path: string;
  method: string;
  body?: unknown;
  params: Record<string, unknown>;
  headers: Record<string, string>;
  timeout: number;
  retry: Partial<RetryConfig> | false;
  skipAuth: boolean;
  rawResponse: boolean;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  timeout?: number;
  retry?: Partial<RetryConfig> | false;
  skipAuth?: boolean;
  rawResponse?: boolean;
}

type RequestInterceptor = (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;
type ResponseInterceptor = (response: unknown, config: RequestConfig) => unknown | Promise<unknown>;
type ErrorInterceptor = (error: ApiError, config: RequestConfig) => unknown | Promise<unknown>;

// =============================================================================
// CONFIGURATION
// =============================================================================

const DEFAULT_CONFIG: ApiClientConfig = {
  baseUrl: API_URL,
  timeout: API_TIMEOUT_DEFAULT,
  retry: {
    attempts: API_RETRY_ATTEMPTS,
    delay: API_RETRY_DELAY,
    retryableStatuses: [408, 429, 500, 502, 503, 504],
    retryOnNetworkError: true
  },
  headers: {
    'Content-Type': 'application/json'
  }
};

// =============================================================================
// ERROR CLASSES
// =============================================================================

/**
 * Error type categories for UI display
 * Maps to specific components/styles in ErrorState.js
 */
export const ERROR_TYPES: Record<string, ErrorType> = {
  NETWORK: 'network',
  SERVER: 'server',
  NOT_FOUND: 'not_found',
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  TIMEOUT: 'timeout',
  VALIDATION: 'validation',
  RATE_LIMIT: 'server',
  CONFLICT: 'server',
  GENERIC: 'unknown'
} as const;

/**
 * Map backend error codes to UI error types
 */
const ERROR_CODE_TO_TYPE: Record<string, ErrorType> = {
  // Backend error codes (from backend/exceptions.py)
  'VALIDATION_ERROR': 'validation',
  'MISSING_FIELD': 'validation',
  'INVALID_FIELD': 'validation',
  'UNAUTHORIZED': 'unauthorized',
  'TOKEN_EXPIRED': 'unauthorized',
  'INVALID_TOKEN': 'unauthorized',
  'FORBIDDEN': 'forbidden',
  'INSUFFICIENT_PERMISSIONS': 'forbidden',
  'NOT_FOUND': 'not_found',
  'PLAN_NOT_FOUND': 'not_found',
  'COURSE_NOT_FOUND': 'not_found',
  'USER_NOT_FOUND': 'not_found',
  'CONFLICT': 'server',
  'DUPLICATE': 'server',
  'RATE_LIMIT_EXCEEDED': 'server',
  'USAGE_LIMIT_EXCEEDED': 'server',
  'INTERNAL_ERROR': 'server',
  'EXTERNAL_SERVICE_ERROR': 'server',
  'DATABASE_ERROR': 'server',
  'BAD_GATEWAY': 'server',
  'SERVICE_UNAVAILABLE': 'server',
  'GATEWAY_TIMEOUT': 'timeout',
  'TIMEOUT': 'timeout',
  'NETWORK_ERROR': 'network',
  // Legacy/fallback codes
  'BAD_REQUEST': 'validation',
  'SERVER_ERROR': 'server',
  'RATE_LIMITED': 'server'
};

/**
 * Map HTTP status codes to UI error types (fallback when no error_code)
 */
const STATUS_TO_TYPE: Record<number, ErrorType> = {
  400: 'validation',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not_found',
  408: 'timeout',
  409: 'server',
  422: 'validation',
  429: 'server',
  500: 'server',
  502: 'server',
  503: 'server',
  504: 'timeout'
};

export interface ApiErrorDetails {
  error_code?: ApiErrorCode;
  field_errors?: Record<string, string[]>;
  missing_fields?: string[];
  field?: string;
  isNetworkError?: boolean;
  isTimeout?: boolean;
  originalError?: Error;
  [key: string]: unknown;
}

/**
 * Custom API error with additional context
 */
export class ApiError extends Error {
  status: number;
  code: string;
  details: ApiErrorDetails;
  isApiError: boolean = true;

  constructor(message: string, status: number, code: string, details: ApiErrorDetails = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.isApiError = true;
  }

  /**
   * Get the UI error type for this error
   * Used for displaying appropriate error components/styles
   */
  getErrorType(): ErrorType {
    // First, try to map from error code (most specific)
    if (this.code && ERROR_CODE_TO_TYPE[this.code]) {
      return ERROR_CODE_TO_TYPE[this.code];
    }

    // Fall back to status code mapping
    if (this.status && STATUS_TO_TYPE[this.status]) {
      return STATUS_TO_TYPE[this.status];
    }

    // Network errors
    if (this.status === 0 || this.details?.isNetworkError) {
      return 'network';
    }

    return 'unknown';
  }

  /**
   * Check if this error is retryable
   */
  isRetryable(): boolean {
    const retryableTypes: ErrorType[] = ['network', 'server', 'timeout'];
    return retryableTypes.includes(this.getErrorType());
  }

  /**
   * Check if this is an authentication error requiring re-login
   */
  requiresReauth(): boolean {
    return this.getErrorType() === 'unauthorized';
  }

  /**
   * Get field-specific errors for form display
   * Returns object like: { email: ['Invalid format'], password: ['Too short'] }
   */
  getFieldErrors(): Record<string, string[]> | null {
    if (this.details?.field_errors) {
      return this.details.field_errors;
    }
    if (this.details?.missing_fields) {
      const errors: Record<string, string[]> = {};
      this.details.missing_fields.forEach(field => {
        errors[field] = ['This field is required'];
      });
      return errors;
    }
    if (this.details?.field) {
      return { [this.details.field]: [this.message] };
    }
    return null;
  }

  static fromResponse(status: number, message: string, details: ApiErrorDetails = {}): ApiError {
    const errorMap: Record<number, { code: string; defaultMsg: string }> = {
      400: { code: 'BAD_REQUEST', defaultMsg: 'Invalid request' },
      401: { code: 'UNAUTHORIZED', defaultMsg: 'Authentication required. Please log in again.' },
      403: { code: 'FORBIDDEN', defaultMsg: 'Access denied. You do not have permission.' },
      404: { code: 'NOT_FOUND', defaultMsg: 'Resource not found.' },
      408: { code: 'TIMEOUT', defaultMsg: 'Request timed out.' },
      409: { code: 'CONFLICT', defaultMsg: 'Resource conflict.' },
      422: { code: 'VALIDATION_ERROR', defaultMsg: 'Validation failed.' },
      429: { code: 'RATE_LIMITED', defaultMsg: 'Too many requests. Please wait.' },
      500: { code: 'SERVER_ERROR', defaultMsg: 'Internal server error.' },
      502: { code: 'BAD_GATEWAY', defaultMsg: 'Server temporarily unavailable.' },
      503: { code: 'SERVICE_UNAVAILABLE', defaultMsg: 'Service temporarily unavailable.' },
      504: { code: 'GATEWAY_TIMEOUT', defaultMsg: 'Server timeout.' }
    };

    // Prefer backend error_code if provided
    const backendCode = details.error_code;
    const errorInfo = errorMap[status] || { code: 'UNKNOWN_ERROR', defaultMsg: 'An error occurred.' };

    return new ApiError(
      message || (details as Record<string, unknown>).error as string || errorInfo.defaultMsg,
      status,
      backendCode || errorInfo.code,
      details
    );
  }

  static networkError(message: string = 'Network error. Please check your connection.'): ApiError {
    return new ApiError(message, 0, 'NETWORK_ERROR', { isNetworkError: true });
  }

  static timeout(message: string = 'Request timed out.'): ApiError {
    return new ApiError(message, 408, 'TIMEOUT', { isTimeout: true });
  }
}

/**
 * Get error type from any error object
 * Useful for components that receive errors from various sources
 */
export const getErrorType = (error: unknown): ErrorType => {
  if (error instanceof ApiError) {
    return error.getErrorType();
  }

  const errorObj = error as { code?: string; status?: number; message?: string } | null;

  // Handle plain error objects with code/status
  if (errorObj?.code && ERROR_CODE_TO_TYPE[errorObj.code]) {
    return ERROR_CODE_TO_TYPE[errorObj.code];
  }

  if (errorObj?.status && STATUS_TO_TYPE[errorObj.status]) {
    return STATUS_TO_TYPE[errorObj.status];
  }

  // Check for common error patterns
  if (errorObj?.message?.toLowerCase().includes('network')) {
    return 'network';
  }

  if (errorObj?.message?.toLowerCase().includes('timeout')) {
    return 'timeout';
  }

  return 'unknown';
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get Supabase JWT access token via TokenManager.
 *
 * This delegates to the centralized TokenManager which:
 * - Caches tokens from onAuthStateChange
 * - Deduplicates concurrent refresh requests
 * - Distinguishes temporary vs permanent refresh errors
 */
const getAccessToken = async (): Promise<string | null> => {
  try {
    const token = await getValidAccessToken();

    // If no token returned, it could mean:
    // 1. User is not authenticated (normal case)
    // 2. Refresh failed permanently (requires re-login)
    // The TokenManager handles distinguishing these cases internally
    return token;
  } catch (error) {
    console.error('Error getting access token:', error);

    // Only trigger auth error for permanent failures
    if (isPermanentRefreshError(error as Error)) {
      handleAuthError();
    }

    return null;
  }
};

/**
 * Unwrap standardized API response format
 * Handles both: {success: true, data: {...}} and direct {...}
 */
export const unwrapResponse = <T>(response: unknown): T => {
  const resp = response as { success?: boolean; data?: T } | null;
  if (resp && typeof resp === 'object' && 'data' in resp) {
    return {
      success: resp.success !== false,
      ...resp.data
    } as T;
  }
  return response as T;
};

/**
 * Sleep for specified milliseconds
 */
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate delay with exponential backoff and jitter
 */
const calculateBackoff = (attempt: number, baseDelay: number): number => {
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 0.3 * exponentialDelay; // 0-30% jitter
  return Math.min(exponentialDelay + jitter, 30000); // Cap at 30 seconds
};

/**
 * Build full URL from path
 */
const buildUrl = (baseUrl: string, path: string, params: Record<string, unknown> = {}): string => {
  const url = new URL(path.startsWith('http') ? path : `${baseUrl}${path}`);

  // Add query parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach(v => url.searchParams.append(key, String(v)));
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  });

  return url.toString();
};

// =============================================================================
// API CLIENT CLASS
// =============================================================================

class ApiClient {
  private config: ApiClientConfig;
  private interceptors: {
    request: RequestInterceptor[];
    response: ResponseInterceptor[];
    error: ErrorInterceptor[];
  };

  constructor(config: Partial<ApiClientConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.interceptors = {
      request: [],
      response: [],
      error: []
    };
  }

  /**
   * Add a request interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): () => void {
    this.interceptors.request.push(interceptor);
    return () => {
      const index = this.interceptors.request.indexOf(interceptor);
      if (index > -1) this.interceptors.request.splice(index, 1);
    };
  }

  /**
   * Add a response interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): () => void {
    this.interceptors.response.push(interceptor);
    return () => {
      const index = this.interceptors.response.indexOf(interceptor);
      if (index > -1) this.interceptors.response.splice(index, 1);
    };
  }

  /**
   * Add an error interceptor
   */
  addErrorInterceptor(interceptor: ErrorInterceptor): () => void {
    this.interceptors.error.push(interceptor);
    return () => {
      const index = this.interceptors.error.indexOf(interceptor);
      if (index > -1) this.interceptors.error.splice(index, 1);
    };
  }

  /**
   * Run request interceptors
   */
  private async runRequestInterceptors(config: RequestConfig): Promise<RequestConfig> {
    let currentConfig = config;
    for (const interceptor of this.interceptors.request) {
      currentConfig = await interceptor(currentConfig);
    }
    return currentConfig;
  }

  /**
   * Run response interceptors
   */
  private async runResponseInterceptors(response: unknown, config: RequestConfig): Promise<unknown> {
    let currentResponse = response;
    for (const interceptor of this.interceptors.response) {
      currentResponse = await interceptor(currentResponse, config);
    }
    return currentResponse;
  }

  /**
   * Run error interceptors
   */
  private async runErrorInterceptors(error: ApiError, config: RequestConfig): Promise<unknown> {
    let currentError = error;
    for (const interceptor of this.interceptors.error) {
      try {
        // Interceptor can either throw (continue error chain) or return (recover)
        const result = await interceptor(currentError, config);
        if (result !== undefined) return result; // Recovered
      } catch (e) {
        currentError = e as ApiError;
      }
    }
    throw currentError;
  }

  /**
   * Core request method
   */
  async request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
    const {
      method = 'GET',
      body,
      params = {},
      headers = {},
      timeout = this.config.timeout,
      retry = this.config.retry,
      skipAuth = false,
      rawResponse = false,
      ...fetchOptions
    } = options;

    // Build initial config
    let config: RequestConfig = {
      path,
      method: method.toUpperCase(),
      body,
      params,
      headers: { ...this.config.headers, ...headers },
      timeout,
      retry,
      skipAuth,
      rawResponse,
      ...fetchOptions
    };

    // Run request interceptors
    config = await this.runRequestInterceptors(config);

    // Add auth header
    if (!config.skipAuth) {
      const accessToken = await getAccessToken();
      if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
      }
    }

    // Build URL
    const url = buildUrl(this.config.baseUrl, config.path, config.params);

    // Prepare fetch options
    const fetchOpts: RequestInit = {
      method: config.method,
      headers: config.headers
    };

    if (config.body && config.method !== 'GET' && config.method !== 'HEAD') {
      fetchOpts.body = typeof config.body === 'string'
        ? config.body
        : JSON.stringify(config.body);
    }

    // Execute with retry logic
    // When retry is disabled, still include retryableStatuses to prevent runtime errors
    const retryConfig: RetryConfig = config.retry === false
      ? { attempts: 1, retryableStatuses: [], retryOnNetworkError: false, delay: 0 }
      : { ...this.config.retry, ...(config.retry as Partial<RetryConfig>) };
    let lastError: ApiError | undefined;
    let retried401 = false;
    // Set when this response must not be sent again — either its status is not
    // retryable at all, or it is retryable but this was the final attempt. The
    // `throw error` below is caught by this method's own outer catch, whose only
    // exit condition was attempt exhaustion — so a 400/403/404/409 was silently
    // re-sent `attempts` times (observed: one click → 3x
    // `PUT /api/admin/feedback/8`, each a 400). A rejected request cannot succeed
    // by being repeated, and for non-idempotent verbs the repeats are actively
    // harmful.
    let stopRetrying = false;

    for (let attempt = 0; attempt < retryConfig.attempts; attempt++) {
      try {
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), config.timeout);

        try {
          const response = await fetch(url, {
            ...fetchOpts,
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          // Handle non-OK responses
          if (!response.ok) {
            const errorText = await response.text().catch(() => '');
            let errorDetails: ApiErrorDetails = {};

            try {
              errorDetails = JSON.parse(errorText);
            } catch {
              errorDetails = { message: errorText } as unknown as ApiErrorDetails;
            }

            const error = ApiError.fromResponse(
              response.status,
              (errorDetails as Record<string, unknown>).message as string ||
              (errorDetails as Record<string, unknown>).error as string ||
              errorText,
              errorDetails
            );

            // 401 retry: force-refresh the token and retry once before failing.
            // This handles the race condition where requests go out during a
            // token refresh (stale/missing token) and get rejected by the backend.
            if (response.status === 401 && !config.skipAuth && !retried401) {
              retried401 = true;
              try {
                const newToken = await forceRefreshToken();
                if (newToken) {
                  config.headers['Authorization'] = `Bearer ${newToken}`;
                  attempt--; // Compensate for loop increment
                  continue;
                }
              } catch {
                // Force refresh failed, proceed with normal error handling
              }
            }

            // Check if we should retry this status
            if (retryConfig.retryableStatuses.includes(response.status) && attempt < retryConfig.attempts - 1) {
              lastError = error;
              const delay = calculateBackoff(attempt, retryConfig.delay);
              await sleep(delay);
              continue;
            }

            stopRetrying = true;
            throw error;
          }

          // Parse response
          let data: unknown;
          const contentType = response.headers.get('content-type');

          if (config.rawResponse) {
            data = response;
          } else if (contentType?.includes('application/json')) {
            data = await response.json();
            data = unwrapResponse(data);
          } else {
            data = await response.text();
          }

          // Run response interceptors
          data = await this.runResponseInterceptors(data, config);

          return data as T;

        } catch (fetchError) {
          clearTimeout(timeoutId);

          // Handle abort (timeout)
          if ((fetchError as Error).name === 'AbortError') {
            throw ApiError.timeout();
          }

          throw fetchError;
        }

      } catch (error) {
        const err = error as Error & { isApiError?: boolean };
        const isNetworkError = !!(err.name === 'TypeError' && err.message?.includes('fetch'));

        if (isNetworkError) {
          lastError = ApiError.networkError();
        } else if (err.isApiError) {
          lastError = err as ApiError;
        } else {
          lastError = new ApiError(err.message || 'Unknown error', 0, 'UNKNOWN_ERROR', { originalError: err });
        }

        // Reaching this catch with `stopRetrying` still false means we have no
        // usable answer from the server: either no response arrived (dropped
        // connection, client-side timeout) or one arrived that we failed to consume
        // (a 200 whose `response.json()` throws, or whose response interceptor
        // throws). Any response we DID consume either `continue`d above (retryable
        // status, attempts left) or set `stopRetrying` before throwing.
        //
        // In every one of those cases we cannot know what the server committed, so
        // re-sending a mutation can duplicate it — strictly worse than surfacing
        // the failure: a timed-out POST the server DID commit becomes two commits.
        // (This also fixes a quieter case: a POST that fully succeeded and only
        // failed while parsing its response used to be re-sent.)
        //
        // NOT covered here, deliberately: status-based retries are method-agnostic,
        // so a 5xx on a mutation is still re-sent — see issue #374. And the 401
        // branch above re-sends regardless of this flag or `retry: false` — #376.
        if (!stopRetrying && !SAFE_METHODS.has(config.method) && !retryConfig.retryUnsafeMethods) {
          stopRetrying = true;
        }

        // Decide explicitly. Previously, when none of the branches matched — a safe
        // method (or an opted-in unsafe one) failing with something that is not a
        // network `TypeError` and never produced a status, i.e. a client-side
        // timeout or an unexpected throw — control fell off the end of this catch
        // and the `for` loop simply re-issued the request. That implicit path had
        // no `sleep`, so it re-sent with ZERO backoff: measured at 3 sends in 2ms.
        // `users.sync` opting into `retryUnsafeMethods` put that on the login path,
        // which is what made a latent defect (#379) worth closing here.
        //
        // `retryOnNetworkError` now governs every transport-level failure, not just
        // the `TypeError` shape. A client-side timeout IS a transport failure, so
        // one flag meaning "re-send after the transport failed" is the coherent
        // reading — and it is behaviour-neutral today, because the only place that
        // flag is false is inside the `retry: false` literal, where `attempts` is 1.
        const isFinalAttempt = attempt === retryConfig.attempts - 1;
        if (!stopRetrying && !isFinalAttempt && retryConfig.retryOnNetworkError) {
          await sleep(calculateBackoff(attempt, retryConfig.delay));
          continue;
        }

        // Give up: either the request must not be re-sent, or attempts are spent.
        try {
          return await this.runErrorInterceptors(lastError, config) as T;
        } catch (interceptedError) {
          throw interceptedError;
        }
      }
    }

    // Should not reach here, but just in case
    throw lastError || new ApiError('Request failed', 0, 'UNKNOWN_ERROR');
  }

  // Convenience methods
  get<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  post<T = unknown>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  put<T = unknown>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }

  patch<T = unknown>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  delete<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

export const apiClient = new ApiClient();

// Module-scoped active-institution slug. Written by ActiveInstitutionContext
// on mount + on every setActive() call. Read by the interceptor below.
// Lives outside React's component tree so the interceptor (which runs in
// promise chains, not render frames) can read it synchronously.
//
// Pre-auth + post-signout state: null — the interceptor sends no header and
// the backend resolves DEFAULT (single-domain model; the subdomain host
// fallback was removed with the 2026-07-17 subdomain teardown).
let _activeSlug: string | null = null;

/** Set the active institution slug for outgoing API requests. Called by
 *  ActiveInstitutionContext when memberships load and on user-initiated
 *  switches. Passing null clears the override (backend resolves DEFAULT). */
export function setActiveSlugForApi(slug: string | null): void {
  _activeSlug = slug;
}

/** Read the active-slug override. Exported for testability. */
export function getActiveSlugForApi(): string | null {
  return _activeSlug;
}

// Add global request interceptor to attach `X-Institution-Slug` header.
// The backend's institution-resolution middleware accepts this header as
// the highest-priority resolver. Sent only when an active institution is
// known (memberships loaded); pre-auth requests carry no header and the
// backend resolves DEFAULT. (The subdomain host fallback was removed with
// the 2026-07-17 subdomain teardown — single domain, no vanity subdomains.)
apiClient.addRequestInterceptor((config) => {
  if (_activeSlug) {
    config.headers['X-Institution-Slug'] = _activeSlug;
  }
  return config;
});

// Add global error interceptor for auth errors.
// This only triggers signout for PERMANENT auth failures.
// Transient 401s (missing/expired tokens during refresh) are handled by
// the 401 retry mechanism in the request method above.
apiClient.addErrorInterceptor((error) => {
  if (error.status === 401) {
    // Only sign out if the error indicates a permanent token failure.
    // After the 401 retry already attempted a token refresh, a second 401
    // with these messages means the user genuinely needs to re-authenticate.
    if (isPermanentRefreshError({ message: error.message })) {
      handleAuthError();
    }
  }
  // Always re-throw to let the caller handle the error too
  throw error;
});

export { getValidAccessToken, isPermanentRefreshError, forceRefreshToken } from '../lib/tokenManager';

export default apiClient;
